import React, { useEffect, useState, useCallback } from 'react';
import { useUserContext } from '../../peregrine/lib/context/user';
import { wishlistToConfiguratorsArray } from './useSavedConfigurators';
import groupBy from '../../lib/util/groupBy';
import gql from 'graphql-tag';
import { useToasts } from '../../peregrine/lib/Toasts';
import Icon from '../../components/Icon';

// see all icons here: https://feathericons.com/
import {
    Star as SuccessIcon,
    AlertCircle as AlertCircleIcon
} from 'react-feather';
import { useQuery, useMutation } from '@apollo/client';
import { updateProductsInWishlistMutation } from '../../queries/updateProductsInWishlist.gql';

const successIcon = (
    <Icon
        src={SuccessIcon}
        attrs={{
            width: 18
        }}
    />
);

const errorIcon = (
    <Icon
        src={AlertCircleIcon}
        attrs={{
            width: 18
        }}
    />
);

const GET_CUSTOMER_WHISHLIST = gql`
    query {
        customer {
            wishlists {
                id
                items_count
                items_v2(currentPage: 1, pageSize: 20) {
                    items {
                        id
                        description
                        product {
                            id
                            sku
                        }
                    }
                }
            }
        }
    }
`;

const ADD_PRODUCT_TO_WISHLIST = gql`
    mutation($id: ID!, $sku: String!) {
        addProductsToWishlist(
            wishlistId: $id
            wishlistItems: [{ sku: $sku, quantity: 1 }]
        ) {
            __typename
            user_errors {
                code
                message
            }
            wishlist {
                id
                items_v2 {
                    items {
                        id
                        description
                        product {
                            id
                            sku
                        }
                    }
                }
            }
        }
    }
`;

const DEFAULT_PROJECT_TITLE = _('Default project');

export const useSaveConfiguration = props => {
    const { label, product, onSaveComplete } = props;

    const [{ isSignedIn }] = useUserContext();
    const [, { addToast }] = useToasts();

    const [wishlists, setWishlists] = useState();
    const [groups, setGroups] = useState();

    const { loading: loadingWishlists, error: errorWishlist, data } = useQuery(
        GET_CUSTOMER_WHISHLIST,
        {
            skip: !isSignedIn
        }
    );

    useEffect(() => {
        if (data && data.customer) {
            setWishlists(data.customer.wishlists);
        }
    }, [data]);

    useEffect(() => {
        const groupedWishlists = groupBy(
            wishlistToConfiguratorsArray(wishlists),
            'group'
        );

        const groups = Object.keys(groupedWishlists);

        setGroups(groups.length ? groups : [DEFAULT_PROJECT_TITLE]);
    }, [wishlists]);

    const [addProductToWishlist] = useMutation(ADD_PRODUCT_TO_WISHLIST);

    const [updateProductInWishlist] = useMutation(
        updateProductsInWishlistMutation
    );

    const [isAddingToWishlist, setIsAddingToWishlist] = useState();

    const saveProductToWishlist = useCallback(
        async group => {
            const { id } = wishlists[0];
            const {
                sku,
                name,
                customPrice,
                customOptionId,
                customOptionValue,
                deepLink
            } = product;

            setIsAddingToWishlist(true);

            try {
                const response = await addProductToWishlist({
                    variables: {
                        id,
                        sku
                    }
                });

                const {
                    user_errors,
                    wishlist
                } = response.data.addProductsToWishlist;

                if (user_errors.length) {
                    throw Error(
                        user_errors.map(({ message }) => message).join('\n')
                    );
                }

                const wishlistItem = wishlist.items_v2.items.find(
                    item => item.product.sku == product.sku
                );

                let description;

                try {
                    description = JSON.parse(wishlistItem.description) || [];
                } catch (error) {
                    console.error(error);
                    description = [];
                }

                description.push({
                    id: new Date() - 0,
                    sku,
                    title: name,
                    customPrice,
                    customOptionId,
                    customOptionValue,
                    date: new Date(),
                    url: deepLink,
                    group
                });

                const response2 = await updateProductInWishlist({
                    variables: {
                        wishlistId: wishlist.id,
                        itemId: wishlistItem.id,
                        configuration: JSON.stringify(description)
                    }
                });

                const {
                    user_errors: user_errors2
                } = response2.data.updateProductsInWishlist;

                if (user_errors2.length) {
                    throw Error(
                        user_errors2.map(({ message }) => message).join('\n')
                    );
                }

                addToast({
                    type: 'info',
                    icon: successIcon,
                    message: _('The current product configuration is saved'),
                    dismissable: true
                });

                onSaveComplete && onSaveComplete();
            } catch (error) {
                addToast({
                    type: 'error',
                    icon: errorIcon,
                    message: error + '',
                    dismissable: true
                });
            } finally {
                setIsAddingToWishlist(false);
            }
        },
        [wishlists, product]
    );

    return {
        isSignedIn,
        label,
        onSaveComplete,
        isAddingToWishlist,
        groups,
        saveProductToWishlist
    };
};
